import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Users, Languages, Compass, Sparkles, ArrowRight } from "lucide-react";
import api from "@/lib/api";

function PrefixMark({ text, prefixes }) {
  if (!prefixes.length) return text;
  const re = new RegExp(`(^|[^a-z0-9])(${prefixes.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
  const out = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    const start = m.index + m[1].length;
    out.push(text.slice(last, start), <mark key={start} className="rounded-sm bg-gold/30 px-0.5 text-foreground">{m[2]}</mark>);
    last = start + m[2].length;
  }
  out.push(text.slice(last));
  return out;
}

const KIND_ICON = { passage: BookOpen, person: Users, word: Languages, theme: Compass, book: BookOpen, suggest: Sparkles };

export function useDebounced(value, ms = 250) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

/** Live "suggest" strip under the /search box: passage lookup, people, Strong's word, Did-you-mean. Results area stays still until commit. */
export function SuggestStrip({ term, committed, onPick, onNavigate }) {
  const debounced = useDebounced(term.trim());
  const active = debounced.length >= 2 && debounced !== committed;
  const { data } = useQuery({
    queryKey: ["suggest", debounced],
    queryFn: async () => (await api.get("/search", { params: { q: debounced, limit: 4 } })).data,
    enabled: active,
    placeholderData: (prev) => prev,
  });
  if (!active || !data) return null;
  const items = [];
  if (data.intent === "passage" && data.reference) {
    const r = data.reference;
    items.push({ kind: "passage", label: r.invalid ? `${r.label || r.book} — ${r.invalid}` : `Look up ${r.label}`, q: r.label || r.book });
  }
  if (data.intent === "word" && data.word) items.push({ kind: "word", label: `Strong's ${data.word.strongs}${data.word.gloss ? ` · ${data.word.gloss}` : ""}`, q: data.word.strongs });
  for (const p of data.groups?.people || []) items.push({ kind: "person", label: p.name, extra: `${p.count}`, q: p.name });
  for (const sg of data.suggestions || []) if (!items.some((i) => i.q === sg.q)) items.push({ kind: sg.kind === "person" ? "person" : sg.kind === "theme" ? "theme" : "suggest", label: sg.label, q: sg.q });
  if (items.length === 0) return null;
  const prefix = debounced.split(/\s+/).filter((t) => t.length >= 2 && !/^\d+$/.test(t));
  return (
    <div className="mt-3 flex max-w-2xl flex-wrap items-center gap-2" data-testid="search-suggest-strip" role="listbox" aria-label="Suggestions">
      {items.slice(0, 6).map((it) => {
        const Icon = KIND_ICON[it.kind] || ArrowRight;
        return (
          <button key={`${it.kind}-${it.q}`} type="button" role="option" aria-selected={false} onClick={() => (it.href && onNavigate ? onNavigate(it.href) : onPick(it.q))}
            data-testid={`suggest-${it.kind}-${it.q.replace(/\W+/g, "-")}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-gold/50 bg-gold/10 px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-gold/20">
            <Icon className="h-3.5 w-3.5 text-gold" />
            <span><PrefixMark text={it.label} prefixes={prefix} /></span>
            {it.extra && <span className="font-mono text-[10px] text-muted-foreground">{it.extra}</span>}
          </button>
        );
      })}
    </div>
  );
}
